import styled from "styled-components";

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;

  @media (max-width: 768px) {
    padding: 2.5rem;
  }

  @media (max-width: 500px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 20px;
  color: #fff;

  @media (max-width: 768px) {
    font-size: 1.725rem;
    text-align: center;
  }

  @media (max-width: 500px) {
    width: 250px;
  }
`;

const Section = styled.section`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  margin-bottom: 10px;
  color: #fff;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 10px;
  margin-left: 2rem;
  color: #ccc;
`;

const ChildSectionTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 10px;
  color: #fff;
`;

const StrongContent = styled.strong`
  color: #fff;
`;

const Terms = () => {
  return (
    <Container>
      <Title>Hotel Booking Terms of Service</Title>
      <Paragraph style={{ marginLeft: 0 }}>
        <strong>Effective Date: 26 Mar 2025</strong>
      </Paragraph>
      <Paragraph style={{ marginLeft: 0 }}>
        By using our hotel booking services, you ("Subscriber" or "You") agree
        to the following terms and conditions. Please read this document
        carefully before making any bookings.
      </Paragraph>

      <Section>
        <SectionTitle>1. Subscriber Information</SectionTitle>
        <Paragraph>
          By becoming a subscriber, you agree to provide accurate, complete, and
          up-to-date information in your account. Failure to do so may result in
          booking cancellations or forfeiture of services. Subscribers must
          ensure that their contact information, including email addresses, are
          correct to receive booking notifications and other important
          communications.
        </Paragraph>
        <Paragraph>
          All subscribers must ensure that the name used on their hotel booking
          matches the name provided in their subscription account. If the names
          do not match, the booking will be forfeited, and the subscriber will
          be required to create a new account with the correct information. The
          subscription account is non-transferable, and only the registered
          subscriber can utilize the booking services under their account.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>2. Hotel Reservations Information</SectionTitle>
        <ChildSectionTitle>2.1 Booking Confirmation</ChildSectionTitle>
        <Paragraph>
          Upon confirmation of your booking, you will receive a confirmation
          email or notification containing your booking details (dates, hotel
          name, room type, etc.). This confirmation serves as proof of your
          reservation. It is your responsibility to review the booking
          information and notify us of any discrepancies within 24 hours. If no
          discrepancies are reported, the booking will be considered final.
        </Paragraph>

        <ChildSectionTitle>2.2 Modifications to Booking</ChildSectionTitle>
        <Paragraph>
          Subscribers may modify or amend bookings, subject to availability and
          any applicable fees or penalties as per the hotel’s policy. However,
          modifications to bookings are{" "}
          <StrongContent>
            subject to the cancellation policy (Section 3)
          </StrongContent>{" "}
          and the Non-Last Rate Category Room requirement (Section 4). Please
          contact our customer service team to request modifications. We will do
          our best to accommodate changes, but we cannot guarantee availability
          or the same rate as the original booking.
        </Paragraph>

        <ChildSectionTitle>2.3 Cancellation Policy</ChildSectionTitle>
        <Paragraph>
          Subscribers are required to cancel any booking at least 48 hours prior
          to the scheduled check-in date. Failure to cancel within this time
          frame will result in a 1-day penalty of cancellation. This penalty
          will be applied automatically to any future bookings or charges
          associated with the account.
        </Paragraph>

        <ChildSectionTitle>
          2.4 Non-Last Rate Category Room & Blackout Periods
        </ChildSectionTitle>
        <Paragraph>
          Subscribers must adhere to the Non-Last Rate Category Room. This is
          required in case the hotel sells out or during blackout periods. In
          these instances, the booking can still be made, but it will be at the
          rack rate provided by the hotel. Please note that rack rates may be
          higher than the standard subscription rates, and you will be charged
          accordingly.
        </Paragraph>

        <ChildSectionTitle>2.5 Room Availability</ChildSectionTitle>
        <Paragraph>
          In the event that a hotel room is not available, we will not proceed
          with the booking. We will notify you immediately if this occurs. Under
          no circumstances will we be held liable for room unavailability, and
          you agree that we will not be responsible for any inconvenience caused
          by the unavailability of a room.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>3. Payments and Fees</SectionTitle>
        <ChildSectionTitle>3.1 Payment Terms</ChildSectionTitle>
        <Paragraph>
          All payments are due directly to the hotel. After booking is
          confirmed, the hotel may request a deposit. To ensure the booking is
          successful, you must complete and return the required deposit form
          within 48 hours. If the deposit form is not submitted within this time
          frame, the booking will be voided, and the reservation will be
          canceled.
        </Paragraph>
        <Paragraph>
          Any additional fees, taxes, or charges imposed by the hotel (e.g.,
          resort fees, parking fees, etc.) are the responsibility of the
          subscriber. These fees will be directly payable to the hotel upon
          check-in or check-out. We do not control these fees and are not liable
          for any discrepancies or charges that arise.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>4. Hotel Policies</SectionTitle>
        <Paragraph>
          Each hotel has its own policies and regulations regarding
          check-in/check-out times, guest behavior, smoking, and pet policies,
          among others. Subscribers are required to adhere to these
          hotel-specific policies during their stay. Failure to do so may result
          in penalties, including but not limited to extra charges or
          termination of the booking. It is your responsibility to verify and
          comply with the hotel’s policies before confirming the booking.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>5. Overbooking and Disputes</SectionTitle>
        <Paragraph>
          In the rare event of overbooking by the hotel, we will make reasonable
          efforts to provide an alternative booking at a comparable hotel or
          assist you in securing other accommodation. We are not liable for any
          inconvenience, cost, or damages incurred due to overbooking. All
          disputes regarding hotel bookings should be resolved directly with the
          hotel. However, we will assist in liaising with the hotel if
          necessary.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>6. Limitation of Liability</SectionTitle>
        <Paragraph>
          We act as a booking intermediary and provide access to fixed rates
          directly provided by the hotels. All rates are set by the hotels, and
          we do not control or adjust these rates. As such, we do not assume
          liability for any discrepancies in rates, changes in hotel policies,
          or any other issues arising from your booking. By using our service,
          you acknowledge and accept that we are not liable for any actions or
          omissions made by the hotel, nor are we responsible for any claims or
          issues related to the booking. Your agreement is solely with the hotel
          when you confirm your reservation.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>7. Privacy and Data Protection</SectionTitle>
        <Paragraph>
          We respect your privacy and are committed to protecting your personal
          information. All personal details provided during the booking process
          are stored and processed in accordance with our Privacy Policy, which
          can be found on our website. By using our service, you consent to the
          collection, use, and sharing of your information in accordance with
          our Privacy Policy.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. Force Majeure</SectionTitle>
        <Paragraph>
          We will not be liable for any failure or delay in performance of any
          part of our services if such failure or delay is caused by
          circumstances beyond our reasonable control, including but not limited
          to acts of God, natural disasters, wars, strikes, or government
          actions.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>9. Termination</SectionTitle>
        <Paragraph>
          We reserve the right to suspend or terminate any subscriber’s account
          at our discretion if there is any breach of these terms, misuse of the
          service, or fraudulent activities. In case of termination, all
          bookings under that account will be canceled, and any penalties or
          forfeitures will apply.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>10. Amendments to Terms of Service</SectionTitle>
        <Paragraph>
          We reserve the right to update or amend these Terms of Service at any
          time. Any changes will be posted on our website, and the revised
          version will be effective immediately upon publication. By continuing
          to use our services after such changes, you agree to the updated
          terms.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>11. Governing Law</SectionTitle>
        <Paragraph>
          These Terms of Service will be governed by and construed in accordance
          with the laws of the State of Delaware. Any disputes arising from or
          related to these Terms of Service shall be subject to the exclusive
          jurisdiction of the courts in the Court of Delaware.
        </Paragraph>
      </Section>

      <Paragraph>
        By booking with us, you acknowledge and agree to abide by the terms
        outlined above. If you have any questions or concerns, please contact
        our customer service team at{" "}
        <a style={{ color: "#3461ff" }} href="mailto:partnerships@kardo.ai">partnerships@kardo.ai</a>.
      </Paragraph>
    </Container>
  );
};

export default Terms;
